import { motion } from "framer-motion";
import {
  Eye, Heart, Briefcase, HeartHandshake, Sparkles, Flame, Star,
  Clock, Compass, Moon, BookOpen, RefreshCw, Link2,
} from "lucide-react";

const SECTIONS = [
  { key: "aussehen", title: "Aussehen der Person", icon: Eye, span: 2 },
  { key: "charakter", title: "Charakter & Persönlichkeit", icon: Heart, span: 2 },
  { key: "beruf", title: "Beruf & Lebensweg", icon: Briefcase, span: 1 },
  { key: "beduerfnisse", title: "Bedürfnisse in der Liebe", icon: HeartHandshake, span: 1 },
  { key: "wuensche", title: "Wünsche an eine Beziehung", icon: Sparkles, span: 1 },
  { key: "beziehungsstil", title: "Wie er/sie in Beziehungen ist", icon: Flame, span: 1 },
  { key: "beziehungsvorstellung", title: "Wie er/sie sich Beziehung vorstellt", icon: Star, span: 2 },
  { key: "erkennung", title: "Wie du ihn/sie erkennst", icon: Compass, span: 1 },
  { key: "zeitpunkt", title: "Wann er/sie in dein Leben tritt", icon: Clock, span: 1 },
  { key: "ort", title: "Wo du ihn/sie kennenlernst", icon: Moon, span: 2 },
];

function SectionCard({ section, text, index }) {
  const Icon = section.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 2) * 0.1 }}
      whileHover={{ y: -4 }}
      data-testid={`result-section-${section.key}`}
      className={`glass gold-border rounded-2xl p-6 sm:p-8 transition-colors hover:border-[#D4AF37]/40 ${
        section.span === 2 ? "lg:col-span-2" : "lg:col-span-1"
      }`}
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/25">
          <Icon className="w-5 h-5 text-[#D4AF37]" />
        </span>
        <h3 className="font-display text-2xl md:text-3xl text-white">{section.title}</h3>
      </div>
      <p className="text-base md:text-lg leading-relaxed text-white/75 whitespace-pre-line">{text}</p>
    </motion.div>
  );
}

export default function Results({ data, onRestart }) {
  const { name, analysis, cards } = data;

  return (
    <section className="relative px-4 sm:px-6 py-20 max-w-6xl mx-auto">
      {/* Intro */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        className="text-center mb-16"
      >
        <p className="text-xs uppercase tracking-[0.28em] text-[#D4AF37] mb-4">Deine Seelenanalyse</p>
        <h2 className="font-display font-light italic text-4xl md:text-5xl lg:text-6xl text-glow-gold mb-6">
          {name}, das sagen die Karten
        </h2>
        {analysis.einleitung && (
          <p className="text-base md:text-lg leading-relaxed text-white/75 max-w-3xl mx-auto">
            {analysis.einleitung}
          </p>
        )}
      </motion.div>

      {/* Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
        {SECTIONS.map((s, i) => (
          <SectionCard key={s.key} section={s} text={analysis[s.key]} index={i} />
        ))}
      </div>

      {/* Seelenbotschaft */}
      {analysis.seelenbotschaft && (
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          data-testid="result-section-seelenbotschaft"
          className="relative mt-6 rounded-2xl p-8 sm:p-12 text-center overflow-hidden glass gold-border"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-[#6D28D9]/10 pointer-events-none" />
          <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-5 animate-float" />
          <h3 className="font-display text-2xl md:text-3xl text-white mb-4">Deine Seelenbotschaft</h3>
          <p className="font-display italic text-xl md:text-2xl leading-relaxed text-white/85 max-w-3xl mx-auto">
            „{analysis.seelenbotschaft}“
          </p>
        </motion.div>
      )}

      {/* Welche Karten */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="mt-16 glass gold-border rounded-2xl p-6 sm:p-10"
        data-testid="cards-used-section"
      >
        <div className="flex items-center gap-3 mb-6 justify-center">
          <BookOpen className="w-6 h-6 text-[#D4AF37]" />
          <h3 className="font-display text-2xl md:text-3xl text-white">Welche Karten wir verwendet haben</h3>
        </div>
        <p className="text-center text-white/60 mb-8 max-w-2xl mx-auto leading-relaxed">
          Lenormandkarten zeigen Ort, Kennenlernen und äußere Hinweise. Tarotkarten offenbaren Charakter,
          Bedürfnisse und Beziehungsthemen. Diese Kombination liefert dir eine präzise, klare und
          tiefgehende Analyse.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {cards.map((c, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/80"
            >
              <span className="text-[#D4AF37] text-xs uppercase tracking-wider">{c.deck}</span>
              <span>{c.name}</span>
            </span>
          ))}
        </div>
      </motion.div>

      {/* Restart */}
      <div className="text-center mt-16">
        <button
          data-testid="restart-button"
          onClick={onRestart}
          className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white/80 font-medium tracking-wide px-8 py-3 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
        >
          <RefreshCw className="w-4 h-4" />
          Neue Analyse erstellen
        </button>
      </div>

      {/* Deployment note */}
      <div className="mt-20 pt-10 border-t border-white/10 text-center">
        <div className="flex items-center gap-2 justify-center mb-3">
          <Link2 className="w-4 h-4 text-[#D4AF37]" />
          <span className="text-xs uppercase tracking-[0.22em] text-[#D4AF37]">Hinweis für App‑Nutzer</span>
        </div>
        <p className="text-sm text-white/50 max-w-2xl mx-auto leading-relaxed">
          Sobald deine Single‑App vollständig fertiggestellt und veröffentlicht ist, erhältst du einen
          öffentlichen Live‑Link. Dieser Link ist dauerhaft erreichbar und kann auf deiner Webseite
          eingebunden werden, damit alle Besucher sofort auf die App zugreifen können.
        </p>
      </div>
    </section>
  );
}
